/**
 * ErrorBoundary Component
 * Catches React errors and provides graceful fallback UI
 */

import React, { Component, ErrorInfo, ReactNode, CSSProperties } from 'react';
import { Button, Text } from '@fluentui/react-components';
import { ErrorCircleRegular, ArrowClockwiseRegular } from '@fluentui/react-icons';

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    minHeight: '400px',
    textAlign: 'center',
  },
  icon: {
    fontSize: '72px',
    color: '#d13438',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '12px',
  },
  message: {
    fontSize: '14px',
    color: '#605e5c',
    marginBottom: '24px',
    maxWidth: '500px',
  },
  details: {
    fontSize: '12px',
    color: '#a19f9d',
    fontFamily: 'monospace',
    backgroundColor: '#f5f5f5',
    padding: '12px',
    borderRadius: '4px',
    marginTop: '12px',
    maxWidth: '600px',
    overflow: 'auto',
    textAlign: 'left',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
};

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div style={styles.container} role="alert" aria-live="assertive">
          <ErrorCircleRegular style={styles.icon} />
          <Text style={styles.title}>Something went wrong</Text>
          <Text style={styles.message}>
            We're sorry, but something unexpected happened. Please try refreshing the page or
            contact support if the problem persists.
          </Text>

          <div style={styles.actions}>
            <Button
              appearance="primary"
              icon={<ArrowClockwiseRegular />}
              onClick={this.handleReload}
            >
              Reload Page
            </Button>
            <Button appearance="secondary" onClick={this.handleReset}>
              Try Again
            </Button>
          </div>

          {/* Show error details in development */}
          {isDevelopment && this.state.error && (
            <details style={styles.details}>
              <summary style={{ cursor: 'pointer', marginBottom: '8px', fontWeight: '600' }}>
                Error Details (Development Only)
              </summary>
              <div>
                <strong>Error:</strong> {this.state.error.toString()}
              </div>
              {this.state.errorInfo && (
                <div style={{ marginTop: '8px' }}>
                  <strong>Component Stack:</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', marginTop: '4px' }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
